import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Filter, Package, Edit, Trash2, Eye, Truck } from "lucide-react";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { getBestBadgeVariant, formatDate } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { purchasesAPI } from "@/lib/api";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { HubFilters } from "@/features/sales/hubFilters";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface Purchase {
	id: string;
	reference?: string;
	supplierName?: string;
	supplier?: {
		id: string;
		name: string;
	};
	totalAmount: number;
	status: "pending" | "approved" | "received" | "cancelled" | "ordered";
	paymentStatus?: "paid" | "partial" | "unpaid" | "installment";
	purchaseDate?: string;
	expectedDelivery?: string;
	createdAt: string;
	paidAmount?: number;
	remainingAmount?: number;
}

const PurchasesPage: React.FC<{ filters?: HubFilters }> = ({ filters }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const currency = useCurrency();
	const { isRTL } = useLanguage();
	const { toast } = useToast();

	const [purchases, setPurchases] = useState<Purchase[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalPurchases, setTotalPurchases] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// When embedded in the sales hub, the hub toolbar owns the filters
	useEffect(() => {
		if (!filters) return;
		setSearchTerm(filters.search);
		setSelectedStatus(filters.status);
		setDateFrom(filters.dateFrom);
		setDateTo(filters.dateTo);
		setCurrentPage(1);
	}, [filters]);

	useEffect(() => {
		fetchPurchases();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when filters change
	}, [currentPage, itemsPerPage, searchTerm, selectedStatus, dateFrom, dateTo]);

	const fetchPurchases = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const params: any = {
				page: currentPage,
				limit: itemsPerPage,
			};

			if (searchTerm) {
				params.search = searchTerm;
			}
			if (selectedStatus) {
				params.status = selectedStatus;
			}
			if (dateFrom) {
				params.startDate = dateFrom;
			}
			if (dateTo) {
				params.endDate = dateTo;
			}

			const response = await purchasesAPI.getAll(params);
			const purchasesData = response.purchases || [];
			const total = response.total || 0;

			// Map API response to Purchase interface
			const mappedPurchases: Purchase[] = purchasesData.map((purchase: any) => ({
				id: purchase.id,
				reference: purchase.reference || purchase.id,
				supplierName: purchase.supplier?.name || purchase.supplierName || "",
				supplier: purchase.supplier,
				totalAmount: parseFloat(purchase.totalAmount || 0),
				status: purchase.status || "pending",
				paymentStatus: purchase.paymentStatus,
				purchaseDate: purchase.purchaseDate || purchase.createdAt,
				expectedDelivery: purchase.expectedDeliveryDate ?? purchase.expectedDelivery,
				createdAt: purchase.createdAt,
				paidAmount: parseFloat(purchase.paidAmount || 0),
				remainingAmount: parseFloat(purchase.remainingAmount || purchase.totalAmount || 0),
			}));

			setPurchases(mappedPurchases);
			setTotalPurchases(total);
			setTotalPages(Math.ceil(total / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			setError(t("purchases.errors.fetchFailed"));
			console.error("Purchases fetch error:", err);
			toast({
				title: t("common.error"),
				description: t("purchases.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeletePurchase = async (purchaseId: number | string) => {
		if (await confirmAction(t("purchases.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await purchasesAPI.delete(purchaseId);
				setPurchases(prev => prev.filter(purchase => purchase.id !== purchaseId));
				setTotalPurchases(prev => prev - 1);
				toast({
					title: t("purchases.messages.deleteSuccess"),
					description: t("purchases.messages.deleteSuccessDescription"),
				});
				// Refresh the list
				fetchPurchases();
			} catch (err) {
				toast({
					title: t("purchases.messages.deleteError"),
					description: t("purchases.messages.deleteErrorDescription"),
					variant: "destructive",
				});
			}
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			pending: {
				variant: "warning",
				text: t("purchases.statuses.pending"),
			},
			approved: {
				variant: "success",
				text: t("purchases.statuses.approved"),
			},
			confirmed: {
				variant: "success",
				text: t("purchases.statuses.confirmed") || t("purchases.statuses.approved"),
			},
			received: {
				variant: "success",
				text: t("purchases.statuses.received"),
			},
			cancelled: {
				variant: "danger",
				text: t("purchases.statuses.cancelled"),
			},
			refunded: {
				variant: "destructive",
				text: t("purchases.statuses.refunded") || "Refunded",
			},
		};

		const config = statusConfig[status as keyof typeof statusConfig] || {
			variant: "secondary",
			text: status || t("common.unknown"),
		};
		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	if (isLoading && currentPage === 1) {
		return <Loading />;
	}

	return (
		<div className={`space-y-6 ${isRTL ? "text-right" : "text-left"}`}>
			{/* Filters and Search */}
			{!filters && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between py-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<Filter className="h-5 w-5" />
							{t("purchases.searchAndFilters")}
						</CardTitle>
						<Button
							variant="link"
							className="text-destructive"
							onClick={() => {
								setSearchTerm("");
								setSelectedStatus("");
								setDateFrom("");
								setDateTo("");
								setCurrentPage(1);
							}}
						>
							{t("purchases.clearFilters")}
						</Button>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
							<div className="relative">
								<Search
									className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`}
								/>
								<Input
									placeholder={t("purchases.searchPlaceholder")}
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									className={isRTL ? "pr-10" : "pl-10"}
								/>
							</div>

							<select
								value={selectedStatus}
								onChange={e => setSelectedStatus(e.target.value)}
								className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<option value="">{t("purchases.allStatuses")}</option>
								<option value="pending">{t("purchases.statuses.pending")}</option>
								<option value="approved">{t("purchases.statuses.approved")}</option>
								<option value="received">{t("purchases.statuses.received")}</option>
								<option value="cancelled">{t("purchases.statuses.cancelled")}</option>
							</select>

							<Input
								type="date"
								placeholder={t("purchases.fromDate")}
								value={dateFrom}
								onChange={e => setDateFrom(e.target.value)}
							/>

							<Input
								type="date"
								placeholder={t("purchases.toDate")}
								value={dateTo}
								onChange={e => setDateTo(e.target.value)}
							/>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Purchases Table */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div className="space-y-2">
						<CardTitle className="flex items-center gap-2 text-base">
							<Package className="w-6 h-6 shrink-0" />
							<span>{t("purchases.title")}</span>
						</CardTitle>
						<CardDescription>{t("purchases.description")}</CardDescription>
					</div>
					{!filters && (
						<PermissionGate permission="purchases.create">
							<Button className="flex items-center gap-2" onClick={() => navigate("/purchases/new")}>
								<Plus className="h-4 w-4" />
								{t("purchases.newPurchase")}
							</Button>
						</PermissionGate>
					)}
				</CardHeader>
				<CardContent>
					<div className="record-surface">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("purchases.table.number")}</TableHead>
									<TableHead>{t("purchases.table.supplierName")}</TableHead>
									<TableHead className="text-center">{t("purchases.table.purchaseDate")}</TableHead>
									<TableHead className="text-center">
										{t("purchases.table.expectedDelivery")}
									</TableHead>
									<TableHead className="text-center">{t("purchases.table.totalAmount")}</TableHead>
									<TableHead className="text-center">{t("purchases.table.status")}</TableHead>
									<TableHead className="text-center">{t("purchases.table.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{!isLoading && purchases.length === 0 && (
									<TableRow>
										<TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
											{t("common.noData")}
										</TableCell>
									</TableRow>
								)}
								{purchases.map((purchase, index) => (
									<TableRow key={purchase.id}>
										<TableCell>{purchase.id}</TableCell>
										<TableCell>{purchase.supplierName || purchase.supplier?.name || "-"}</TableCell>
										<TableCell className="text-center text-muted-foreground">
											{purchase.purchaseDate
												? formatDate(purchase.purchaseDate)
												: formatDate(purchase.createdAt)}
										</TableCell>
										<TableCell className="text-center">
											<div className="flex justify-center items-center gap-2">
												<Truck className="h-4 w-4 text-muted-foreground" />
												{purchase.expectedDelivery
													? formatDate(purchase.expectedDelivery)
													: "-"}
											</div>
										</TableCell>
										<TableCell className="text-center font-medium">
											<Price value={Number(purchase.totalAmount)} currency={currency} />
										</TableCell>
										<TableCell className="text-center">{getStatusBadge(purchase.status)}</TableCell>
										<TableCell className="text-center">
											<div className="flex items-center justify-center gap-2">
												<PermissionGate permission="purchases.read">
													<Button
														variant="outline"
														title={t("common.view")}
														aria-label={t("common.view")}
														size="icon"
														onClick={() => navigate(`/purchases/${purchase.id}`)}
													>
														<Eye className="h-4 w-4" />
													</Button>
												</PermissionGate>
												<PermissionGate permission="purchases.update">
													<Button
														variant="outline"
														title={t("common.edit")}
														aria-label={t("common.edit")}
														size="icon"
														onClick={() => navigate(`/purchases/${purchase.id}/edit`)}
													>
														<Edit className="h-4 w-4 text-primary" />
													</Button>
												</PermissionGate>
												<PermissionGate permission="purchases.delete">
													<Button
														variant="outline"
														title={t("common.delete")}
														aria-label={t("common.delete")}
														size="icon"
														onClick={() => handleDeletePurchase(purchase.id)}
													>
														<Trash2 className="h-4 w-4 text-destructive" />
													</Button>
												</PermissionGate>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalPurchases}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName="purchases.purchases"
			/>

			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<Package className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("purchases.errorLoading")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchPurchases}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default PurchasesPage;
